import React, { useEffect, useCallback, useState } from 'react';
import './ImageViewer.css';

function getImageSrc(images, index) {
  const photo = images[index];
  const linkObj = Array.isArray(photo?.link) ? photo.link[0] : photo?.link;
  return linkObj?.uri || linkObj?.thumbnail_url;
}

export default function ImageViewer({ images, currentIndex, onClose, onPrev, onNext }) {
  const [isLoading, setIsLoading] = useState(true);

  const photo = images[currentIndex];
  const linkObj = Array.isArray(photo?.link) ? photo.link[0] : photo?.link;
  const src = linkObj?.uri || linkObj?.thumbnail_url;
  const title = linkObj?.title || `Image ${currentIndex + 1}`;

  // Reset loading state when image changes
  useEffect(() => {
    setIsLoading(true);
  }, [src]);

  // Preload next and previous images so navigation feels instant
  useEffect(() => {
    const toPreload = [currentIndex - 1, currentIndex + 1];
    toPreload.forEach(i => {
      if (i >= 0 && i < images.length) {
        const url = getImageSrc(images, i);
        if (url) {
          const img = new Image();
          img.src = url;
        }
      }
    });
  }, [currentIndex, images]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') onPrev();
    if (e.key === 'ArrowRight') onNext();
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  return (
    <div className="iv-overlay" onClick={onClose}>
      <button className="iv-close" onClick={onClose} aria-label="Close">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>

      {currentIndex > 0 && (
        <button className="iv-nav iv-nav--prev" onClick={(e) => { e.stopPropagation(); onPrev(); }} aria-label="Previous">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
      )}

      <div className="iv-content" onClick={(e) => e.stopPropagation()}>
        {isLoading && (
          <div className="iv-spinner-wrap">
            <span className="iv-spinner" />
          </div>
        )}
        <img
          className="iv-image"
          src={src}
          alt={title}
          style={{ opacity: isLoading ? 0 : 1 }}
          onLoad={() => setIsLoading(false)}
          onError={() => setIsLoading(false)}
        />
        <div className="iv-footer">
          <span className="iv-title">{title}</span>
          <span className="iv-counter">{currentIndex + 1} / {images.length}</span>
        </div>
      </div>

      {currentIndex < images.length - 1 && (
        <button className="iv-nav iv-nav--next" onClick={(e) => { e.stopPropagation(); onNext(); }} aria-label="Next">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      )}
    </div>
  );
}
